/**
 * SetlistPlayer — auto-cycling YouTube iframe that plays one track after another.
 *
 *  - Uses YouTube IFrame API (loaded once, globally) to detect the `ended` state
 *    on each track and advance to the next.
 *  - Auto-skips tracks that error (embed disabled / removed / region-blocked).
 *  - Calls `onAllFailed` if every track in the setlist errors so the parent can
 *    fall back to alternate audio (e.g. the synthesised Hologram Score).
 *  - Exposes a "Track 2 of 5" overlay so investors can read the setlist.
 *
 * Props:
 *   setlist:        string[]         array of YouTube watch URLs
 *   fallbackUrl?:   string           single YT URL when setlist is empty
 *   muted?:         boolean
 *   paused?:        boolean
 *   onTrackChange?: (idx) => void
 *   onAllFailed?:   () => void       fired when every track in `setlist` errored
 *   className?:     string
 *   coverMode?:     boolean          oversize 130% to crop YT chrome
 */
import { useEffect, useRef, useState, useCallback } from "react";

// --------------- YT IFrame API loader (singleton) ---------------
let _ytApiPromise = null;
function loadYouTubeApi() {
  if (typeof window === "undefined") return Promise.resolve(null);
  if (window.YT && window.YT.Player) return Promise.resolve(window.YT);
  if (_ytApiPromise) return _ytApiPromise;
  _ytApiPromise = new Promise((resolve) => {
    const tag = document.createElement("script");
    tag.src = "https://www.youtube.com/iframe_api";
    document.head.appendChild(tag);
    const prev = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      if (typeof prev === "function") prev();
      resolve(window.YT);
    };
  });
  return _ytApiPromise;
}

function ytId(url) {
  if (!url) return null;
  const m = url.match(
    /(?:youtube\.com\/(?:watch\?v=|embed\/|v\/|shorts\/)|youtu\.be\/)([a-zA-Z0-9_-]{6,})/
  );
  return m ? m[1].split(/[?&#]/)[0] : null;
}

export default function SetlistPlayer({
  setlist = [],
  fallbackUrl = null,
  muted = false,
  paused = false,
  onTrackChange,
  onAllFailed,
  className = "",
  coverMode = false,
  testId = "setlist-player",
}) {
  const tracks = (setlist?.length ? setlist : fallbackUrl ? [fallbackUrl] : [])
    .map(ytId)
    .filter(Boolean);
  const [idx, setIdx] = useState(0);
  const containerRef = useRef(null);
  const playerRef = useRef(null);
  const idxRef = useRef(0);
  const failedRef = useRef(new Set());

  // Build the iframe via YT API so we can subscribe to onStateChange / `ended`.
  useEffect(() => {
    if (!tracks.length || !containerRef.current) return;
    let destroyed = false;
    let div = null;
    loadYouTubeApi().then((YT) => {
      if (destroyed || !YT) return;
      // Ensure the host div is fresh
      containerRef.current.innerHTML = "";
      div = document.createElement("div");
      div.style.width = "100%";
      div.style.height = "100%";
      containerRef.current.appendChild(div);
      playerRef.current = new YT.Player(div, {
        videoId: tracks[0],
        host: "https://www.youtube-nocookie.com",
        playerVars: {
          autoplay: paused ? 0 : 1,
          mute: muted ? 1 : 0,
          controls: 0,
          rel: 0,
          modestbranding: 1,
          playsinline: 1,
          iv_load_policy: 3,
        },
        events: {
          onReady: (e) => {
            try {
              if (muted) e.target.mute();
              else e.target.unMute();
              if (!paused) e.target.playVideo();
            } catch {}
          },
          onStateChange: (e) => {
            // 0 = ended, advance to next track and loop the setlist
            if (e.data === 0) {
              const next = (idxRef.current + 1) % tracks.length;
              idxRef.current = next;
              setIdx(next);
              try { e.target.loadVideoById(tracks[next]); } catch {}
              if (typeof onTrackChange === "function") onTrackChange(next);
            }
          },
          onError: (e) => {
            // Codes: 100 (not found), 101/150 (embed blocked by owner), 2/5 (player err).
            // Auto-skip to the next track so the demo never lands on "Video unavailable".
            console.warn("[SetlistPlayer] YT error", e.data, "→ skipping track");
            failedRef.current.add(idxRef.current);
            // If every track in the setlist has now failed, give up and let the
            // parent fall back to alternate audio (Hologram Score).
            if (failedRef.current.size >= tracks.length) {
              if (typeof onAllFailed === "function") onAllFailed();
              return;
            }
            // Find the next non-failed track
            let next = (idxRef.current + 1) % tracks.length;
            let attempts = 0;
            while (failedRef.current.has(next) && attempts < tracks.length) {
              next = (next + 1) % tracks.length;
              attempts++;
            }
            if (next === idxRef.current) return;
            idxRef.current = next;
            setIdx(next);
            try { e.target.loadVideoById(tracks[next]); } catch {}
            if (typeof onTrackChange === "function") onTrackChange(next);
          },
        },
      });
    });
    return () => {
      destroyed = true;
      try { playerRef.current?.destroy?.(); } catch {}
      playerRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tracks.join("|")]);

  // External pause/play sync
  useEffect(() => {
    const p = playerRef.current;
    if (!p?.playVideo) return;
    try {
      if (paused) p.pauseVideo();
      else p.playVideo();
    } catch {}
  }, [paused]);

  // External mute sync
  useEffect(() => {
    const p = playerRef.current;
    if (!p?.mute) return;
    try {
      if (muted) p.mute();
      else p.unMute();
    } catch {}
  }, [muted]);

  const skipNext = useCallback(() => {
    const p = playerRef.current;
    if (!p?.loadVideoById || !tracks.length) return;
    const next = (idxRef.current + 1) % tracks.length;
    idxRef.current = next;
    setIdx(next);
    try { p.loadVideoById(tracks[next]); } catch {}
    if (typeof onTrackChange === "function") onTrackChange(next);
  }, [tracks, onTrackChange]);

  if (!tracks.length) return null;

  return (
    <div
      className={`bg-black overflow-hidden ${className}`}
      data-testid={testId}
    >
      <div
        ref={containerRef}
        className="absolute hologram-isolated"
        style={
          coverMode
            ? {
                width: "130%",
                height: "130%",
                top: "-15%",
                left: "-15%",
              }
            : { inset: 0, width: "100%", height: "100%" }
        }
      />
      {/* Setlist HUD */}
      {tracks.length > 1 && (
        <div
          className="absolute bottom-2 left-2 right-2 flex items-center gap-2 z-10 pointer-events-none"
          data-testid={`${testId}-hud`}
        >
          <div className="font-mono-ui text-[9px] tracking-[0.32em] uppercase text-[#FFD166] bg-black/70 backdrop-blur px-2 py-1 inline-flex items-center gap-2">
            <span className="ring-dot" style={{ width: 4, height: 4, background: "#FFD166", boxShadow: "0 0 8px #FFD166" }} />
            Track {idx + 1} of {tracks.length}
          </div>
          <div className="flex-1 flex gap-1">
            {tracks.map((_, i) => (
              <div key={i} className="flex-1 h-[2px] bg-white/10">
                <div
                  className="h-full bg-[#FFD166] transition-all"
                  style={{ width: i < idx ? "100%" : i === idx ? "50%" : "0%" }}
                />
              </div>
            ))}
          </div>
          <button
            onClick={skipNext}
            className="font-mono-ui text-[9px] tracking-[0.28em] uppercase text-white/70 hover:text-[#FFD166] bg-black/70 backdrop-blur px-2 py-1 pointer-events-auto"
            data-testid={`${testId}-next`}
          >
            Skip ▸
          </button>
        </div>
      )}
    </div>
  );
}
